import React from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  FiArrowDown,
  FiArrowUp,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiHelpCircle,
  FiImage,
  FiPlus,
  FiRotateCcw,
  FiSave,
  FiTrash2,
  FiUpload,
} from 'react-icons/fi';

import AuthenticatedGuideMedia from '../components/AuthenticatedGuideMedia';
import GuideHtmlAuthoringHelp from '../components/GuideHtmlAuthoringHelp';
import GuideHtmlRenderer from '../components/GuideHtmlRenderer';
import {
  GuideHelpModalStyles,
  GuideManagementPageStyles,
} from '../components/style';
import {
  GuideMediaAsset,
  GuideTopic,
  UserGuideConfig,
  UserGuideTabId,
} from '../types';
import { GUIDE_HTML_STARTER_TEMPLATE } from '../utils/guideHtmlAuthoring';
import {
  buildCreativeTestingAssetSrc,
  buildMediaEmbedSnippet,
  cloneGuideConfig,
  createGuideId,
  getGuideTab,
  getGuideTopic,
  normalizeGuideConfig,
} from '../utils/userGuide';
import { formatDateTimeDisplayValue, getGuideValidationErrors } from '../utils/management';
import {
  useGetUserGuideQuery,
  useUpdateUserGuideMutation,
  useUploadUserGuideMediaMutation,
} from 'services/api/userGuide';
import useHeader from 'containers/App/Header/useHeader';

const DEFAULT_TAB_ID: UserGuideTabId = 'self-serve-tv-lab';
const GUIDE_ADMIN_DRAFT_STORAGE_KEY = 'tvlabs-user-guide-admin-draft';

const loadStoredGuideDraft = (): UserGuideConfig | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(GUIDE_ADMIN_DRAFT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = normalizeGuideConfig(JSON.parse(raw));
    return parsed ? cloneGuideConfig(parsed) : null;
  } catch {
    return null;
  }
};

const persistGuideDraft = (draft: UserGuideConfig) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    GUIDE_ADMIN_DRAFT_STORAGE_KEY,
    JSON.stringify(draft),
  );
};

const clearStoredGuideDraft = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(GUIDE_ADMIN_DRAFT_STORAGE_KEY);
};

const createBlankTopic = (): GuideTopic => {
  const now = new Date().toISOString();

  return {
    id: createGuideId('topic'),
    title: 'New Topic',
    contentHtml: '<h2>New Topic</h2><p>Add the guide content here.</p>',
    createdAt: now,
    updatedAt: now,
  };
};

const getMediaType = (file: File): GuideMediaAsset['type'] =>
  file.type.startsWith('video/') ? 'video' : 'image';

const getUploadErrorMessage = (error: any) => {
  if (typeof error?.data?.error === 'string' && error.data.error.trim()) {
    return error.data.error;
  }

  if (typeof error?.error === 'string' && error.error.trim()) {
    return error.error;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Unable to upload media. Please try again.';
};

export default function UserGuideManagementPage() {
  useHeader('User Guide Manager');

  const restoredDraft = React.useMemo(() => loadStoredGuideDraft(), []);
  const [activeTabId, setActiveTabId] =
    React.useState<UserGuideTabId>(DEFAULT_TAB_ID);
  const [activeTopicId, setActiveTopicId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<UserGuideConfig | null>(
    restoredDraft,
  );
  const [saveMessage, setSaveMessage] = React.useState(
    restoredDraft ? 'Unsaved guide draft restored after refresh.' : '',
  );
  const [saveMessageVariant, setSaveMessageVariant] = React.useState<
    'info' | 'warning' | 'danger' | 'success'
  >(restoredDraft ? 'warning' : 'info');
  const [copyMessage, setCopyMessage] = React.useState('');
  const [authoringMessage, setAuthoringMessage] = React.useState('');
  const [topicsCollapsed, setTopicsCollapsed] = React.useState(false);
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const [editorPaneWidth, setEditorPaneWidth] = React.useState(50);
  const [isResizingEditor, setIsResizingEditor] = React.useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const editorWorkspaceRef = React.useRef<HTMLDivElement | null>(null);

  const { data, error, isFetching, isLoading } = useGetUserGuideQuery();
  const [updateUserGuide, updateState] = useUpdateUserGuideMutation();
  const [uploadUserGuideMedia] = useUploadUserGuideMediaMutation();
  const normalizedLoadedConfig = React.useMemo(
    () => normalizeGuideConfig(data),
    [data],
  );

  React.useEffect(() => {
    if (!normalizedLoadedConfig) return;

    setDraft(currentDraft => currentDraft || cloneGuideConfig(normalizedLoadedConfig));
  }, [normalizedLoadedConfig]);

  const activeTab = draft ? getGuideTab(draft, activeTabId) : undefined;
  const activeTopic = getGuideTopic(activeTab, activeTopicId);
  const validationErrors = draft ? getGuideValidationErrors(draft) : [];
  const hasUnsavedChanges =
    draft && normalizedLoadedConfig
      ? JSON.stringify(draft) !== JSON.stringify(normalizedLoadedConfig)
      : false;

  React.useEffect(() => {
    if (!draft || !normalizedLoadedConfig) return;

    if (JSON.stringify(draft) !== JSON.stringify(normalizedLoadedConfig)) {
      persistGuideDraft(draft);
      return;
    }

    clearStoredGuideDraft();
  }, [draft, normalizedLoadedConfig]);

  React.useEffect(() => {
    if (!draft?.tabs.length) return;

    const nextActiveTab = getGuideTab(draft, activeTabId) || draft.tabs[0];

    if (nextActiveTab.id !== activeTabId) {
      setActiveTabId(nextActiveTab.id);
      return;
    }

    if (!nextActiveTab.topics.some(topic => topic.id === activeTopicId)) {
      setActiveTopicId(nextActiveTab.topics[0]?.id || null);
    }
  }, [activeTabId, activeTopicId, draft]);

  React.useEffect(() => {
    if (!isResizingEditor) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const workspace = editorWorkspaceRef.current;
      if (!workspace) return;

      const rect = workspace.getBoundingClientRect();
      const nextWidth = ((event.clientX - rect.left) / rect.width) * 100;
      const clampedWidth = Math.min(75, Math.max(25, nextWidth));
      setEditorPaneWidth(clampedWidth);
    };

    const handleMouseUp = () => setIsResizingEditor(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingEditor]);

  const updateDraft = (updater: (currentDraft: NonNullable<typeof draft>) => NonNullable<typeof draft>) => {
    setSaveMessage('');
    setSaveMessageVariant('info');
    setDraft(currentDraft => {
      if (!currentDraft) return currentDraft;

      return {
        ...updater(currentDraft),
        updatedAt: new Date().toISOString(),
      };
    });
  };

  const updateActiveTab = (
    updater: (tab: NonNullable<typeof activeTab>) => NonNullable<typeof activeTab>,
  ) => {
    updateDraft(currentDraft => ({
      ...currentDraft,
      tabs: currentDraft.tabs.map(tab =>
        tab.id === activeTabId ? updater(tab) : tab,
      ),
    }));
  };

  const updateActiveTopic = (updates: Partial<GuideTopic>) => {
    if (!activeTopic) return;

    updateActiveTab(tab => ({
      ...tab,
      topics: tab.topics.map(topic =>
        topic.id === activeTopic.id
          ? { ...topic, ...updates, updatedAt: new Date().toISOString() }
          : topic,
      ),
    }));
  };

  const updateTabLabel = (tabId: UserGuideTabId, label: string) => {
    updateDraft(currentDraft => ({
      ...currentDraft,
      tabs: currentDraft.tabs.map(tab =>
        tab.id === tabId
          ? {
              ...tab,
              label,
              layout: {
                ...tab.layout,
                title: label,
              },
            }
          : tab,
      ),
    }));
  };

  const addTopic = () => {
    const topic = createBlankTopic();

    updateActiveTab(tab => ({
      ...tab,
      topics: [...tab.topics, topic],
    }));
    setActiveTopicId(topic.id);
  };

  const removeTopic = (topicId: string) => {
    updateActiveTab(tab => ({
      ...tab,
      topics: tab.topics.filter(topic => topic.id !== topicId),
    }));
  };

  const moveTopic = (topicId: string, direction: -1 | 1) => {
    updateActiveTab(tab => {
      const currentIndex = tab.topics.findIndex(topic => topic.id === topicId);
      const nextIndex = currentIndex + direction;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= tab.topics.length) {
        return tab;
      }

      const topics = [...tab.topics];
      const [topic] = topics.splice(currentIndex, 1);
      topics.splice(nextIndex, 0, topic);

      return { ...tab, topics };
    });
  };

  const uploadMedia = async (files: FileList | null) => {
    if (!files?.length) return;

    try {
      const uploadedAssets = await Promise.all(
        Array.from(files).map(async file => {
          const uploadResult = await uploadUserGuideMedia({ file }).unwrap();

          return {
            id: createGuideId('media'),
            name: uploadResult.name || file.name,
            type: uploadResult.type || getMediaType(file),
            src: buildCreativeTestingAssetSrc(
              uploadResult.path,
              uploadResult.fileName,
            ),
            alt: file.name.replace(/\.[^.]+$/, ''),
            createdAt: uploadResult.uploadedAt || new Date().toISOString(),
            assetKey: uploadResult.key,
            fileName: uploadResult.fileName,
            path: uploadResult.path,
            contentType: uploadResult.contentType || file.type,
            uploadedAt: uploadResult.uploadedAt || new Date().toISOString(),
            uploadedBy: uploadResult.uploadedBy,
          };
        }),
      );

      updateDraft(currentDraft => ({
        ...currentDraft,
        mediaLibrary: [...currentDraft.mediaLibrary, ...uploadedAssets],
      }));
      setSaveMessage('Media uploaded successfully.');
      setSaveMessageVariant('success');
    } catch (uploadError: any) {
      setSaveMessage(getUploadErrorMessage(uploadError));
      setSaveMessageVariant('danger');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeMedia = (assetId: string) => {
    updateDraft(currentDraft => ({
      ...currentDraft,
      mediaLibrary: currentDraft.mediaLibrary.filter(asset => asset.id !== assetId),
    }));
  };

  const copyEmbedSnippet = async (asset: GuideMediaAsset) => {
    const snippet = buildMediaEmbedSnippet(asset);

    try {
      await navigator.clipboard.writeText(snippet);
      setCopyMessage(`Copied embed snippet for ${asset.name}.`);
    } catch {
      setCopyMessage(snippet);
    }
  };

  const discardChanges = () => {
    if (normalizedLoadedConfig) {
      setDraft(cloneGuideConfig(normalizedLoadedConfig));
      clearStoredGuideDraft();
      setSaveMessage('Changes discarded.');
      setSaveMessageVariant('info');
      setAuthoringMessage('');
    }
  };

  const copyStarterTemplate = async () => {
    try {
      await navigator.clipboard.writeText(GUIDE_HTML_STARTER_TEMPLATE);
      setAuthoringMessage('Starter markup copied. Paste it into any topic and replace the placeholders.');
    } catch {
      setAuthoringMessage(
        'Clipboard access is blocked in this browser. Use "Use starter template" to load the markup into the current topic.',
      );
    }
  };

  const useStarterTemplate = () => {
    if (!activeTopic) return;

    const currentHtml = activeTopic.contentHtml.trim();
    const starterHtml = GUIDE_HTML_STARTER_TEMPLATE.trim();
    const hasExistingMarkup = currentHtml && currentHtml !== starterHtml;

    if (
      hasExistingMarkup &&
      typeof window !== 'undefined' &&
      !window.confirm(
        'Replace the current HTML in this topic with the rich starter template?',
      )
    ) {
      return;
    }

    updateActiveTopic({ contentHtml: GUIDE_HTML_STARTER_TEMPLATE });
    setAuthoringMessage(
      'Starter template loaded. Replace the copy, then swap the placeholder block with media embed snippets as needed.',
    );
  };

  const saveChanges = async () => {
    if (!draft || validationErrors.length) return;

    try {
      const savedConfig = normalizeGuideConfig(await updateUserGuide(draft).unwrap());
      if (savedConfig) {
        setDraft(cloneGuideConfig(savedConfig));
      }
      clearStoredGuideDraft();
      setSaveMessage('Guide changes saved.');
      setSaveMessageVariant('success');
    } catch (saveError: any) {
      const status = Number(saveError?.status);
      const errorMessage =
        typeof saveError?.data?.error === 'string' ? saveError.data.error : '';

      if (status === 409 || /changed|conflict/i.test(errorMessage)) {
        if (normalizedLoadedConfig) {
          setDraft(cloneGuideConfig(normalizedLoadedConfig));
        }
        clearStoredGuideDraft();
        setSaveMessage(
          'Guide changed on the server while you were editing. Latest content has been reloaded; please re-apply your edits and save again.',
        );
        setSaveMessageVariant('warning');
        return;
      }

      setSaveMessage(errorMessage || 'Unable to save guide changes. Please try again.');
      setSaveMessageVariant('danger');
    }
  };

  const errorMessage =
    error && 'data' in error
      ? String((error.data as any)?.error || 'Failed to load guide content.')
      : error
        ? 'Failed to load guide content.'
        : undefined;

  if (isLoading || !draft) {
    return (
      <GuideManagementPageStyles>
        <div className="guide-admin-loading">
          <Spinner animation="border" />
          <p>Loading user guide admin...</p>
        </div>
      </GuideManagementPageStyles>
    );
  }

  return (
    <GuideManagementPageStyles>
      <div className="guide-admin-shell">
      <header className="guide-admin-header">
        <div className="guide-admin-title-block">
          <div className="guide-admin-eyebrow">Admin Center</div>
          <h1>User Guide Manager</h1>
          <p>
            Edit published guide content, manage topic order, and maintain
            embedded media from one admin workspace.
          </p>
        </div>
        <h1>User Guide — Admin</h1>
        <div className="guide-admin-actions">
          <Button
            disabled={!hasUnsavedChanges || updateState.isLoading}
            onClick={discardChanges}
            variant="light"
          >
            <FiRotateCcw />
            Discard
          </Button>
          <Button
            disabled={
              !hasUnsavedChanges ||
              updateState.isLoading ||
              Boolean(validationErrors.length)
            }
            onClick={saveChanges}
            variant="secondary"
          >
            <FiSave />
            Save changes
          </Button>
        </div>
      </header>

      {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
      {saveMessage ? <Alert variant={saveMessageVariant}>{saveMessage}</Alert> : null}
      {validationErrors.length ? (
        <Alert variant="warning">
          {validationErrors.map(errorText => (
            <div key={errorText}>{errorText}</div>
          ))}
        </Alert>
      ) : null}

      <section className="guide-settings-card">
        <h2>Settings</h2>
        <div className="guide-settings-grid">
          {draft.tabs.map((tab, index) => (
            <Form.Group key={tab.id}>
              <Form.Label>Tab {index + 1} label</Form.Label>
              <Form.Control
                onChange={event =>
                  updateTabLabel(tab.id, event.currentTarget.value)
                }
                value={tab.label}
              />
            </Form.Group>
          ))}
          <Form.Group>
            <Form.Label>Announcement highlight (days)</Form.Label>
            <Form.Control
              min={1}
              max={90}
              onChange={event => {
                const announcementHighlightDays = Number(
                  event.currentTarget.value,
                );

                updateDraft(currentDraft => ({
                  ...currentDraft,
                  announcementHighlightDays,
                }));
              }}
              type="number"
              value={draft.announcementHighlightDays}
            />
          </Form.Group>
        </div>
      </section>

      <nav className="guide-admin-tabs" role="tablist">
        {draft.tabs.map(tab => (
          <button
            className={tab.id === activeTabId ? 'active' : ''}
            key={tab.id}
            onClick={() => setActiveTabId(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main
        className={`guide-admin-workspace ${
          topicsCollapsed ? 'topics-collapsed' : ''
        }`}
      >
        <aside
          className={`guide-topics-card ${
            topicsCollapsed ? 'is-collapsed' : ''
          }`}
        >
          <div className="guide-topics-card-header">
            <h2>Topics</h2>
            <div className="guide-topics-header-actions">
              {!topicsCollapsed ? (
                <Button onClick={addTopic} variant="light">
                  <FiPlus />
                  Add
                </Button>
              ) : null}
              <button
                type="button"
                className="guide-topic-collapse-button"
                onClick={() => setTopicsCollapsed(current => !current)}
                aria-label={topicsCollapsed ? 'Expand topics' : 'Collapse topics'}
              >
                {topicsCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
              </button>
            </div>
          </div>
          {!topicsCollapsed ? (
          <div className="guide-admin-topic-list">
            {activeTab?.topics.length ? (
              activeTab.topics.map((topic, index) => (
                <button
                  className={topic.id === activeTopicId ? 'active' : ''}
                  key={topic.id}
                  onClick={() => setActiveTopicId(topic.id)}
                  type="button"
                >
                  <span>{topic.title || `Topic ${index + 1}`}</span>
                  <span className="guide-admin-topic-actions">
                    <FiArrowUp
                      aria-hidden
                      className={index === 0 ? 'disabled' : ''}
                      onClick={event => {
                        event.stopPropagation();
                        moveTopic(topic.id, -1);
                      }}
                    />
                    <FiArrowDown
                      aria-hidden
                      className={
                        index === activeTab.topics.length - 1 ? 'disabled' : ''
                      }
                      onClick={event => {
                        event.stopPropagation();
                        moveTopic(topic.id, 1);
                      }}
                    />
                    <FiTrash2
                      aria-hidden
                      className="danger"
                      onClick={event => {
                        event.stopPropagation();
                        removeTopic(topic.id);
                      }}
                    />
                  </span>
                </button>
              ))
            ) : (
              <p className="guide-muted-copy">No topics yet.</p>
            )}
          </div>
          ) : null}
        </aside>

        <section className="guide-editor-card">
          <div className="guide-editor-card-header">
            <div>
              <h2>Edit topic</h2>
              <p>
                Write guide content and review the published rendering side by
                side.
              </p>
            </div>
            <Button
              className="guide-help-button"
              onClick={() => setHelpModalOpen(true)}
              variant="light"
            >
              <FiHelpCircle />
              Get Started
            </Button>
          </div>
          {activeTopic ? (
            <>
              <div
                className="guide-editor-split"
                ref={editorWorkspaceRef}
                style={
                  {
                    '--guide-editor-width': `${editorPaneWidth}%`,
                  } as React.CSSProperties
                }
              >
                <div className="guide-topic-editor-pane">
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  onChange={event =>
                    updateActiveTopic({ title: event.currentTarget.value })
                  }
                  value={activeTopic.title}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>HTML content (right panel)</Form.Label>
                <Form.Control
                  as="textarea"
                  className="guide-html-textarea"
                  onChange={event => {
                    setAuthoringMessage('');
                    updateActiveTopic({ contentHtml: event.currentTarget.value });
                  }}
                  value={activeTopic.contentHtml}
                />
              </Form.Group>
                </div>
                <button
                  aria-label="Resize editor and preview"
                  className="guide-editor-resize-handle"
                  onMouseDown={() => setIsResizingEditor(true)}
                  type="button"
                />
                <div className="guide-preview-pane">
              <div>
                <h3>Live preview</h3>
                <GuideHtmlRenderer
                  className="guide-admin-live-preview guide-html-render-surface"
                  html={activeTopic.contentHtml}
                />
              </div>
                </div>
              </div>
              <p className="guide-muted-copy">
                Created {formatDateTimeDisplayValue(activeTopic.createdAt)}
                {activeTabId === 'announcements'
                  ? ' · Newly added announcements light up the bell icon.'
                  : ''}
              </p>
              {draft.updatedBy ? (
                <p className="guide-muted-copy">
                  Last saved by {draft.updatedBy.name}
                  {draft.updatedBy.email ? ` (${draft.updatedBy.email})` : ''} on{' '}
                  {formatDateTimeDisplayValue(draft.updatedAt)}
                </p>
              ) : null}
            </>
          ) : (
            <div className="guide-empty-card guide-empty-card-compact">
              <h4>Select or add a topic</h4>
              <p>Pick a topic from the left panel to edit its HTML content.</p>
            </div>
          )}
        </section>
      </main>

      <GuideHelpModalStyles
        centered
        onHide={() => setHelpModalOpen(false)}
        show={helpModalOpen}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Get Started</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <GuideHtmlAuthoringHelp
            onCopyTemplate={copyStarterTemplate}
            onUseTemplate={useStarterTemplate}
            statusMessage={authoringMessage}
          />
        </Modal.Body>
      </GuideHelpModalStyles>

      <section className="guide-media-card">
        <div className="guide-media-header">
          <div>
            <h2>
              <FiImage />
              Media library
            </h2>
            <p>
              Upload images or videos, then copy the embed snippet into any
              topic&apos;s HTML.
            </p>
          </div>
          <Button onClick={() => fileInputRef.current?.click()} variant="light">
            <FiUpload />
            Upload
          </Button>
          <input
            accept="image/*,video/*"
            hidden
            multiple
            onChange={event => uploadMedia(event.currentTarget.files)}
            ref={fileInputRef}
            type="file"
          />
        </div>

        {copyMessage ? <Alert variant="success">{copyMessage}</Alert> : null}

        {draft.mediaLibrary.length ? (
          <div className="guide-media-grid">
            {draft.mediaLibrary.map(asset => (
              <article className="guide-media-item" key={asset.id}>
                <div className="guide-media-preview">
                  <AuthenticatedGuideMedia asset={asset} />
                </div>
                <div className="guide-media-details">
                  <strong>{asset.name}</strong>
                  <small>{asset.type}</small>
                </div>
                <div className="guide-media-actions">
                  <Button onClick={() => copyEmbedSnippet(asset)} variant="light">
                    <FiCopy />
                    Copy embed
                  </Button>
                  <Button onClick={() => removeMedia(asset.id)} variant="light">
                    <FiTrash2 />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="guide-muted-copy">No media uploaded.</p>
        )}
      </section>

      {draft.changeLog?.length ? (
        <section className="guide-media-card guide-version-card">
          <div className="guide-media-header">
            <div>
              <h2>Version history</h2>
              <p>Latest saved guide versions and the editor responsible for them.</p>
            </div>
            <Button
              className="guide-version-toggle"
              onClick={() => setVersionHistoryOpen(current => !current)}
              variant="light"
            >
              {versionHistoryOpen ? <FiArrowUp /> : <FiArrowDown />}
              {versionHistoryOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
          {versionHistoryOpen ? (
          <div className="guide-version-list">
            {draft.changeLog.slice(0, 8).map(entry => (
              <div className="guide-muted-copy" key={entry.id}>
                Version {entry.version} · {formatDateTimeDisplayValue(entry.at)} · {entry.actor.name}
                {entry.actor.email ? ` (${entry.actor.email})` : ''} ·{' '}
                {entry.summary || `Saved version ${entry.version}`}
              </div>
            ))}
          </div>
          ) : null}
        </section>
      ) : null}
      </div>
    </GuideManagementPageStyles>
  );
}
